import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useParavetOptionsAPI } from "@/app/hooks/api_hooks/configuration/useParavetOptionsAPI";
import { useClinicParavetMappingAddAPI } from "@/app/hooks/api_hooks/configuration/useClinicParavetMappingAddAPI";
import { IParavetClinicListItem } from "@/api_framework/api_modals/Configuration";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

interface ParavetModalProps {
  open: boolean;
  onClose: () => void;
  clinic: IParavetClinicListItem | null;
  onSubmitSuccess: () => void;
}

const ParavetModalComponent = ({ open, onClose, clinic, onSubmitSuccess }: ParavetModalProps) => {
  const { execute: mapClinicParavet } = useClinicParavetMappingAddAPI();
  const { execute: fetchParavetOptions, paravetOptions, allParavets } = useParavetOptionsAPI();
  const [selectedParavets, setSelectedParavets] = useState([]);

  const formHook = useForm<any>({
    mode: "onChange",
  });

  const onSubmit = async () => {
    const paravetIds = formHook.watch("paravet_id") || [];
    await mapClinicParavet(clinic?.id, { paravet_ids: paravetIds });
    onClose();
    onSubmitSuccess();
  };

  useEffect(() => {
    if (open) {
      fetchParavetOptions();
    }
  }, [open, fetchParavetOptions]);

  useEffect(() => {
    formHook.setValue("paravet_id", clinic?.paravet_ids || []);
  }, [clinic]);

  const paravetOptionsItem = useSelectMenuReducer(paravetOptions, "display_name", "paravet_id");

  useEffect(() => {
    const selectedIds: string[] = formHook.watch("paravet_id") || [];
    setSelectedParavets(
      selectedIds.map((id) => {
        const activeOption = paravetOptionsItem.find((option) => option.id === id);
        if (activeOption) return activeOption;
        const inactive = allParavets.find((paravet) => paravet.paravet_id === id);
        return {
          id,
          title: inactive ? `${inactive.display_name || inactive.name} (inactive)` : `${id} (unavailable)`,
        };
      }) as any,
    );
  }, [formHook.watch("paravet_id"), paravetOptions, allParavets]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-visible rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
                <div className="flex items-start justify-between">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-slate-900">{clinic?.clinic_name}</Dialog.Title>
                    <p className="text-sm text-slate-500">
                      {clinic?.active_paravet_count || 0} paravet(s) currently mapped
                    </p>
                  </div>
                  <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
                    ✕
                  </button>
                </div>

                <div className="mt-4 rounded-xl bg-slate-50 p-3">
                  <p className="text-sm font-semibold text-slate-900">Mapped Paravets</p>
                  {selectedParavets?.length === 0 ? (
                    <p className="mt-2 text-sm text-slate-500">No paravets selected.</p>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedParavets?.map((paravet) => (
                        <span
                          key={paravet.id}
                          className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-sm font-medium text-slate-800 ring-1 ring-slate-200"
                        >
                          {paravet.title}
                          <button
                            type="button"
                            className="text-rose-500 hover:text-rose-600"
                            onClick={() => {
                              const updated = selectedParavets.filter((item) => item.id !== paravet.id);
                              setSelectedParavets(updated);
                              const updatedIds = formHook.watch("paravet_id").filter((id) => id !== paravet.id);
                              formHook.setValue("paravet_id", updatedIds);
                            }}
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <SearchableMultiSelectMenu
                    onSelectItem={(items) => {
                      const ids = items.map((item) => item.id);
                      formHook.setValue("paravet_id", ids);
                    }}
                    withSelectAll
                    showTooltips
                    withDropDownSearch
                    errorMessages={[{ message: "Required", type: "required" }]}
                    fieldError={formHook.formState.errors.paravet_id}
                    register={formHook.register("paravet_id")}
                    showTypedErrors={false}
                    containerClassName="ml-2"
                    placeholder="Select Paravet"
                    defaultSelected={paravetOptionsItem?.filter((c) => formHook.watch("paravet_id")?.includes(c.id))}
                    selectItems={paravetOptionsItem}
                  />
                  <p className="mt-2 ml-2 text-[11px] text-[#6C727F]">
                    Only active paravets can be mapped. Clearing the selection unmaps every paravet from this clinic.
                  </p>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onSubmit}
                    className="rounded-lg bg-black px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-900"
                  >
                    Save Mapping
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ParavetModalComponent;
